<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import baileysChannel from 'dashboard/api/channel/baileysChannel';
import IdentityPanel from './IdentityPanel.vue';
import DiagnosticsPanel from './DiagnosticsPanel.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const emit = defineEmits(['sessionUpdated']);

const { t } = useI18n();
const { hasPermission, isAdministrator } = usePermissions();
const canManageSession = computed(
  () => isAdministrator.value || hasPermission('inbox.manage_session')
);

const status = ref({});
const events = ref([]);
const metrics = ref({});
const health = ref({});
const signalStats = ref({});
const qrDataUrl = ref(null);
const showAdvanced = ref(false);
const showResetConfirm = ref(false);
const resetConfirmText = ref('');
const showLogoutConfirm = ref(false);
const showPairCodeModal = ref(false);
const pairCodePhone = ref('');
const pairCodeResult = ref(null);
const showPurgePeerModal = ref(false);
const purgePeerJid = ref('');
const isLoading = ref({}); // per-command loading flag
const isStale = ref(false); // true quando o bridge não respondeu o último status
const lastStatusOkAt = ref(null);
// Após reset/logout deliberado, não ressuscitar a sessão no auto-heal por N ms.
let suppressAutoHealUntil = 0;
// Estados a partir dos quais faz sentido auto-curar (sessão caiu sozinha).
const RECOVERABLE_STATES = ['connected', 'connecting'];

let statusHandle = null;
let healthHandle = null;
const delayedRefreshHandles = [];

const stateLabel = computed(() => {
  const map = {
    created: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.CREATED'),
    connecting: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.CONNECTING'),
    qr: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.QR'),
    connected: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.CONNECTED'),
    paused: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.PAUSED'),
    disconnected: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.DISCONNECTED'),
    banned: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.BANNED'),
    absent: t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.ABSENT'),
  };
  if (isStale.value) return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.STALE');
  return map[status.value.state] || status.value.state || '-';
});

const stateDotClass = computed(() => {
  if (isStale.value) return 'bg-n-slate-7';
  const s = status.value.state;
  if (s === 'connected') return 'bg-n-teal-9';
  if (s === 'qr' || s === 'connecting') return 'bg-n-amber-9';
  if (s === 'paused') return 'bg-n-slate-9';
  if (s === 'banned' || s === 'disconnected' || s === 'absent')
    return 'bg-n-ruby-9';
  return 'bg-n-slate-7';
});

const lastSeenRelative = computed(() => {
  if (!status.value.last_seen_at) return '-';
  const diff = Math.floor(
    (Date.now() - new Date(status.value.last_seen_at).getTime()) / 1000
  );
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  return `${Math.floor(diff / 3600)}h`;
});

// Auto-heal de sessão fantasma: quando o status volta 404, a sessão sumiu da
// memória do bridge (ex.: caiu para 'disconnected' e o bridge reiniciou). Os
// outros verbos também dariam 404, então disparamos um Start - que passa por
// ensure_session no backend e recria a sessão - sem o cliente precisar acionar
// o super_admin. Debounced p/ não criar tempestade de starts no polling.
let lastAutoHealAt = 0;
const autoHealGhostSession = async () => {
  const now = Date.now();
  if (now - lastAutoHealAt < 30000) return; // no máximo 1 tentativa / 30s
  lastAutoHealAt = now;
  try {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.AUTO_HEAL_TRIGGERED'));
    await baileysChannel.start(props.inbox.id);
    // O polling de status (5s) reflete o novo estado em seguida - sem refresh
    // explícito aqui para não referenciar helpers definidos mais abaixo.
  } catch (_) {
    // Próximo 404 do polling tentará de novo após o debounce.
  }
};

const fetchStatus = async () => {
  try {
    const { data } = await baileysChannel.status(props.inbox.id);
    status.value = data;
    isStale.value = false;
    lastStatusOkAt.value = Date.now();

    if (data.state === 'qr') {
      try {
        const qrRes = await baileysChannel.qr(props.inbox.id);
        qrDataUrl.value = qrRes.data.qr_base64;
      } catch (_) {
        qrDataUrl.value = null;
      }
    } else {
      qrDataUrl.value = null;
    }
  } catch (err) {
    if (err.response?.status === 404) {
      // Sessão ausente no bridge. Só auto-cura se estava de pé
      // (connected/connecting) e não foi destruída deliberadamente agora.
      const recoverable =
        RECOVERABLE_STATES.includes(status.value.state) &&
        Date.now() >= suppressAutoHealUntil;
      if (recoverable) {
        await autoHealGhostSession();
      } else {
        // Honesto: não mostra "Conectado" fóssil - a sessão não existe lá.
        status.value = { ...status.value, state: 'absent' };
        qrDataUrl.value = null;
      }
    } else {
      // Bridge não respondeu (timeout/erro): marca desatualizado em vez de
      // manter o último valor como se fosse atual. A UI nunca mente o estado.
      isStale.value = true;
    }
  }
};

const fetchEvents = async () => {
  try {
    const { data } = await baileysChannel.events(props.inbox.id, 50);
    events.value = data.events || [];
  } catch (_) {
    /* ignore */
  }
};

const fetchMetrics = async () => {
  try {
    const { data } = await baileysChannel.metrics(props.inbox.id);
    metrics.value = data;
  } catch (_) {
    /* ignore */
  }
};

const fetchHealth = async () => {
  try {
    const { data } = await baileysChannel.health(props.inbox.id);
    health.value = data;
  } catch (_) {
    /* ignore */
  }
};

const fetchSignalStats = async () => {
  try {
    const { data } = await baileysChannel.signalStats(props.inbox.id);
    signalStats.value = data;
  } catch (_) {
    /* ignore */
  }
};

const refreshAll = async () => {
  await Promise.all([
    fetchStatus(),
    fetchEvents(),
    fetchMetrics(),
    fetchHealth(),
    fetchSignalStats(),
  ]);
};

const notifySessionUpdated = data => {
  emit('sessionUpdated', data || status.value);
};

const refreshSessionState = async () => {
  await fetchStatus();
  notifySessionUpdated();
};

const scheduleSessionRefresh = () => {
  [1000, 3000].forEach(delay => {
    const handle = setTimeout(() => {
      refreshSessionState();
    }, delay);
    delayedRefreshHandles.push(handle);
  });
};

const healthScoreClass = computed(() => {
  const s = health.value.score;
  if (s === 'healthy') return 'bg-n-teal-3 text-n-teal-12 ring-n-teal-7';
  if (s === 'degraded') return 'bg-n-amber-3 text-n-amber-12 ring-n-amber-7';
  if (s === 'unhealthy') return 'bg-n-ruby-3 text-n-ruby-12 ring-n-ruby-7';
  return 'bg-n-slate-3 text-n-slate-12 ring-n-slate-7';
});

const healthScoreLabel = computed(() => {
  const s = health.value.score;
  if (!s) return '-';
  return t(`INBOX_MGMT.SETTINGS_POPUP.BAILEYS.HEALTH.SCORE_${s.toUpperCase()}`);
});

// Generic command runner - sets loading flag, runs, refreshes status, alerts on error.
const runCommand = async (key, fn, successMessage, options = {}) => {
  const { refreshSession = true, delayedRefresh = false } = options;
  isLoading.value = { ...isLoading.value, [key]: true };
  try {
    const response = await fn();
    if (refreshSession) {
      if (response?.data?.state) {
        status.value = response.data;
        notifySessionUpdated(response.data);
      } else {
        await refreshSessionState();
      }
      if (delayedRefresh) scheduleSessionRefresh();
    }
    if (successMessage) useAlert(successMessage);
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.COMMAND_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, [key]: false };
  }
};

const handleStart = () =>
  runCommand('start', () => baileysChannel.start(props.inbox.id), null, {
    delayedRefresh: true,
  });
const handleStop = () =>
  runCommand('stop', () => baileysChannel.stop(props.inbox.id), null, {
    delayedRefresh: true,
  });
const handleRestart = () =>
  runCommand('restart', () => baileysChannel.restart(props.inbox.id), null, {
    delayedRefresh: true,
  });
const handleReconnect = () =>
  runCommand(
    'reconnect',
    () => baileysChannel.reconnect(props.inbox.id),
    null,
    { delayedRefresh: true }
  );
const handleRegenerateQr = () =>
  runCommand('qr', () => baileysChannel.regenerateQr(props.inbox.id), null, {
    delayedRefresh: true,
  });
const handleClearMedia = () =>
  runCommand(
    'clear_media',
    () => baileysChannel.clearMedia(props.inbox.id),
    t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.MEDIA_CLEARED'),
    { refreshSession: false }
  );

const handleLogout = async () => {
  showLogoutConfirm.value = false;
  // Logout é deliberado: não deixar o auto-heal religar a sessão por baixo.
  suppressAutoHealUntil = Date.now() + 60000;
  await runCommand(
    'logout',
    () => baileysChannel.logout(props.inbox.id),
    t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.LOGGED_OUT'),
    { delayedRefresh: true }
  );
};

// Reset DESTROI a sessao no bridge (DELETE /sessions/:id). Backend devolve
// state sintetico 'created' porque a sessao deixou de existir e o status
// subsequente retornaria 404. Usamos runDestructiveCommand para refletir o
// state na UI imediatamente sem depender do fetchStatus pos-delete.
const runDestructiveCommand = async (key, fn, successMessage) => {
  // Reset destrói a sessão deliberadamente: suprime o auto-heal para o poll
  // seguinte (404) não ressuscitar o que o operador acabou de destruir.
  suppressAutoHealUntil = Date.now() + 60000;
  isLoading.value = { ...isLoading.value, [key]: true };
  try {
    const { data } = await fn();
    if (data && data.state) {
      status.value = { ...status.value, state: data.state };
      qrDataUrl.value = null;
      notifySessionUpdated(status.value);
    }
    if (successMessage) useAlert(successMessage);
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.COMMAND_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, [key]: false };
  }
};

const handleReset = async () => {
  if (resetConfirmText.value !== 'RESET') return;
  showResetConfirm.value = false;
  resetConfirmText.value = '';
  await runDestructiveCommand(
    'reset',
    () => baileysChannel.reset(props.inbox.id),
    t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.RESET_DONE')
  );
  // Reset agora auto-conecta no backend → busca o QR logo (1s/3s) sem esperar
  // o poll de 5s, para o operador ver o novo QR na hora.
  scheduleSessionRefresh();
};

const handlePairCode = async () => {
  const phone = pairCodePhone.value.replace(/\D/g, '');
  if (!phone || phone.length < 10) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PAIR_CODE_INVALID_PHONE'));
    return;
  }
  isLoading.value = { ...isLoading.value, pair_code: true };
  try {
    const { data } = await baileysChannel.pairCode(props.inbox.id, phone);
    pairCodeResult.value = data;
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PAIR_CODE_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, pair_code: false };
  }
};

const closePairCodeModal = () => {
  showPairCodeModal.value = false;
  pairCodePhone.value = '';
  pairCodeResult.value = null;
};

const handlePurgePeer = async () => {
  const jid = purgePeerJid.value.trim();
  if (!jid) return;
  isLoading.value = { ...isLoading.value, purge_peer: true };
  try {
    const { data } = await baileysChannel.purgePeer(props.inbox.id, jid);
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PURGE_PEER_DONE', {
        removed: data.removed || 0,
      })
    );
    showPurgePeerModal.value = false;
    purgePeerJid.value = '';
    await fetchSignalStats();
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PURGE_PEER_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, purge_peer: false };
  }
};

const eventIcon = type => {
  if (type.startsWith('state.connected'))
    return { icon: 'i-lucide-check-circle-2', cls: 'text-n-teal-11' };
  if (type.startsWith('state.qr'))
    return { icon: 'i-lucide-qr-code', cls: 'text-n-amber-11' };
  if (type.startsWith('state.disconnected') || type.startsWith('state.banned'))
    return { icon: 'i-lucide-alert-circle', cls: 'text-n-ruby-11' };
  if (type === 'error')
    return { icon: 'i-lucide-alert-triangle', cls: 'text-n-ruby-11' };
  return { icon: 'i-lucide-circle-dot', cls: 'text-n-slate-11' };
};

const formatDate = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString();
};

onMounted(() => {
  refreshAll();
  // Poll status every 5s so the UI stays fresh without ActionCable.
  statusHandle = setInterval(fetchStatus, 5000);
  // Health is heavier - poll every 30s.
  healthHandle = setInterval(fetchHealth, 30000);
});

onBeforeUnmount(() => {
  if (statusHandle) clearInterval(statusHandle);
  if (healthHandle) clearInterval(healthHandle);
  delayedRefreshHandles.forEach(clearTimeout);
});
</script>

<template>
  <div class="space-y-6">
    <!-- Stale banner: bridge não respondeu o último status (não mente "Conectado") -->
    <div
      v-if="isStale"
      class="flex items-center gap-2 p-3 rounded-xl border border-n-amber-7 bg-n-amber-3 text-sm text-n-amber-12"
    >
      <span class="i-lucide-wifi-off size-4 shrink-0" />
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STALE_WARNING') }}
    </div>

    <!-- Status header -->
    <div class="p-5 rounded-2xl border border-n-weak bg-n-background">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div class="flex items-center gap-3">
          <span
            class="inline-block w-3 h-3 rounded-full"
            :class="stateDotClass"
          />
          <div>
            <div class="flex items-center gap-2">
              <span class="text-base font-semibold text-n-slate-12">{{
                stateLabel
              }}</span>
              <span v-if="status.phone" class="text-sm text-n-slate-11">·</span>
              <span
                v-if="status.phone"
                class="text-sm text-n-slate-11 font-mono"
              >
                {{ status.phone }}
              </span>
            </div>
            <div class="text-xs text-n-slate-11 mt-0.5">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.LAST_SEEN', {
                  time: lastSeenRelative,
                })
              }}
              <span v-if="status.session_id" class="font-mono ml-2">{{
                status.session_id
              }}</span>
            </div>
          </div>
        </div>
        <NextButton ghost icon="i-lucide-refresh-ccw" sm @click="refreshAll" />
      </div>

      <!-- QR inline if pending -->
      <div
        v-if="qrDataUrl"
        class="mt-5 flex flex-col items-center p-4 rounded-lg bg-n-alpha-2"
      >
        <img :src="qrDataUrl" alt="QR" class="w-48 h-48 rounded shadow" />
        <p class="text-xs text-n-slate-11 mt-2">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.QR_HELP') }}
        </p>
        <NextButton
          v-if="canManageSession"
          ghost
          icon="i-lucide-keyboard"
          sm
          class="mt-3"
          @click="showPairCodeModal = true"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.USE_PAIR_CODE') }}
        </NextButton>
      </div>
    </div>

    <!-- Health card - uptime/disconnects/lag visíveis -->
    <div
      v-if="health.score"
      class="p-4 rounded-2xl border border-n-weak bg-n-background"
    >
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.HEALTH.TITLE') }}
        </h4>
        <span
          class="px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset"
          :class="healthScoreClass"
        >
          {{ healthScoreLabel }}
        </span>
      </div>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <div>
          <div class="text-xs text-n-slate-11 uppercase tracking-wider">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.HEALTH.UPTIME_24H') }}
          </div>
          <div class="text-xl font-bold text-n-slate-12 mt-1">
            {{ health.uptime_percent_24h ?? '-' }}%
          </div>
        </div>
        <div>
          <div class="text-xs text-n-slate-11 uppercase tracking-wider">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.HEALTH.DISCONNECTS_24H') }}
          </div>
          <div
            class="text-xl font-bold mt-1"
            :class="
              (health.disconnect_count_24h || 0) > 5
                ? 'text-n-amber-11'
                : 'text-n-slate-12'
            "
          >
            {{ health.disconnect_count_24h ?? '-' }}
          </div>
        </div>
        <div>
          <div class="text-xs text-n-slate-11 uppercase tracking-wider">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.HEALTH.WEBHOOK_LAG_P95') }}
          </div>
          <div
            class="text-xl font-bold mt-1"
            :class="
              (health.webhook_p95_lag_seconds || 0) > 5
                ? 'text-n-ruby-11'
                : 'text-n-slate-12'
            "
          >
            {{
              health.webhook_p95_lag_seconds != null
                ? `${health.webhook_p95_lag_seconds}s`
                : '-'
            }}
          </div>
        </div>
        <div>
          <div class="text-xs text-n-slate-11 uppercase tracking-wider">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.HEALTH.PENDING') }}
          </div>
          <div
            class="text-xl font-bold mt-1"
            :class="
              (health.webhook_pending || 0) > 50
                ? 'text-n-ruby-11'
                : 'text-n-slate-12'
            "
          >
            {{ health.webhook_pending ?? '-' }}
          </div>
        </div>
      </div>
      <div
        v-if="
          health.decrypt_issues_last_hour &&
          health.decrypt_issues_last_hour.length
        "
        class="mt-3 p-3 rounded-lg bg-n-amber-3/40 border border-n-amber-7"
      >
        <div class="text-xs font-semibold text-n-amber-12 mb-1">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.HEALTH.DECRYPT_ISSUES_TITLE')
          }}
        </div>
        <ul class="text-xs text-n-amber-12 space-y-0.5">
          <li
            v-for="(issue, idx) in health.decrypt_issues_last_hour"
            :key="idx"
            class="font-mono"
          >
            {{ issue.payload?.boundary || 'decrypt_failure' }} -
            {{ issue.occurrences }}x
          </li>
        </ul>
      </div>
    </div>

    <!-- Primary commands -->
    <div>
      <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PRIMARY_COMMANDS') }}
      </h4>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <NextButton
          ghost
          icon="i-lucide-play"
          :is-loading="isLoading.start"
          :disabled="
            status.state === 'connected' || status.state === 'connecting'
          "
          @click="handleStart"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.START') }}
        </NextButton>
        <NextButton
          ghost
          icon="i-lucide-pause"
          :is-loading="isLoading.stop"
          :disabled="
            status.state === 'paused' || status.state === 'disconnected'
          "
          @click="handleStop"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STOP') }}
        </NextButton>
        <NextButton
          ghost
          icon="i-lucide-rotate-cw"
          :is-loading="isLoading.restart"
          @click="handleRestart"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.RESTART') }}
        </NextButton>
        <NextButton
          ghost
          icon="i-lucide-zap"
          :is-loading="isLoading.reconnect"
          @click="handleReconnect"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.RECONNECT') }}
        </NextButton>
        <NextButton
          ghost
          icon="i-lucide-qr-code"
          :is-loading="isLoading.qr"
          @click="handleRegenerateQr"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.REGENERATE_QR') }}
        </NextButton>
        <NextButton
          ghost
          icon="i-lucide-trash"
          :is-loading="isLoading.clear_media"
          @click="handleClearMedia"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.CLEAR_MEDIA') }}
        </NextButton>
      </div>
    </div>

    <!-- Destructive commands -->
    <div
      v-if="canManageSession"
      class="p-4 rounded-lg border border-n-ruby-7 bg-n-ruby-3/30"
    >
      <h4 class="text-sm font-semibold text-n-ruby-12 mb-3">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DESTRUCTIVE_COMMANDS') }}
      </h4>
      <div class="flex flex-wrap gap-2">
        <NextButton
          ruby
          ghost
          icon="i-lucide-key-round"
          sm
          @click="showPurgePeerModal = true"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PURGE_PEER') }}
        </NextButton>
        <NextButton
          ruby
          ghost
          icon="i-lucide-log-out"
          sm
          @click="showLogoutConfirm = true"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.LOGOUT') }}
        </NextButton>
        <NextButton
          ruby
          ghost
          icon="i-lucide-flame"
          sm
          @click="showResetConfirm = true"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.RESET') }}
        </NextButton>
      </div>
      <p class="text-xs text-n-ruby-12 mt-3">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.DESTRUCTIVE_HELP') }}
      </p>
    </div>

    <!-- Logout confirm modal -->
    <div
      v-if="showLogoutConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-n-background rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
      >
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.LOGOUT_CONFIRM_TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.LOGOUT_CONFIRM_BODY') }}
        </p>
        <div class="flex justify-end gap-2 mt-5">
          <NextButton ghost @click="showLogoutConfirm = false">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.CANCEL_BUTTON') }}
          </NextButton>
          <NextButton ruby @click="handleLogout">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.LOGOUT_CONFIRM_BUTTON') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- Reset confirm modal -->
    <div
      v-if="showResetConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-n-background rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
      >
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.RESET_CONFIRM_TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.RESET_CONFIRM_BODY') }}
        </p>
        <input
          v-model="resetConfirmText"
          type="text"
          placeholder="RESET"
          class="mt-3 block w-full rounded-md border-0 px-3 py-2 shadow-sm ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-ruby-9 sm:text-sm bg-n-background outline-none font-mono"
        />
        <div class="flex justify-end gap-2 mt-5">
          <NextButton
            ghost
            @click="
              showResetConfirm = false;
              resetConfirmText = '';
            "
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.CANCEL_BUTTON') }}
          </NextButton>
          <NextButton
            ruby
            :disabled="resetConfirmText !== 'RESET'"
            @click="handleReset"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.RESET_CONFIRM_BUTTON') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- Pair Code modal -->
    <div
      v-if="showPairCodeModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-n-background rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
      >
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PAIR_CODE_TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PAIR_CODE_BODY') }}
        </p>
        <input
          v-if="!pairCodeResult"
          v-model="pairCodePhone"
          type="tel"
          placeholder="5527999000000"
          class="mt-3 block w-full rounded-md border-0 px-3 py-2 shadow-sm ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-amber-9 sm:text-sm bg-n-background outline-none font-mono"
        />
        <div
          v-else
          class="mt-4 p-4 rounded-lg bg-n-amber-3/40 border border-n-amber-7 text-center"
        >
          <div class="text-xs text-n-amber-12 uppercase tracking-wider">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PAIR_CODE_LABEL') }}
          </div>
          <div
            class="text-3xl font-mono font-bold text-n-amber-12 mt-1 tracking-widest"
          >
            {{ pairCodeResult.code }}
          </div>
          <div class="text-xs text-n-amber-12 mt-2">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PAIR_CODE_INSTRUCTIONS') }}
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-5">
          <NextButton ghost @click="closePairCodeModal">
            {{
              pairCodeResult
                ? $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.CLOSE_BUTTON')
                : $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.CANCEL_BUTTON')
            }}
          </NextButton>
          <NextButton
            v-if="!pairCodeResult"
            amber
            :is-loading="isLoading.pair_code"
            :disabled="
              !pairCodePhone || pairCodePhone.replace(/\D/g, '').length < 10
            "
            @click="handlePairCode"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PAIR_CODE_GENERATE') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- Purge Peer modal -->
    <div
      v-if="showPurgePeerModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-n-background rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
      >
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PURGE_PEER_TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PURGE_PEER_BODY') }}
        </p>
        <input
          v-model="purgePeerJid"
          type="text"
          placeholder="5527999000000@s.whatsapp.net"
          class="mt-3 block w-full rounded-md border-0 px-3 py-2 shadow-sm ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-ruby-9 sm:text-sm bg-n-background outline-none font-mono"
        />
        <p class="text-xs text-n-slate-11 mt-2">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PURGE_PEER_HELP') }}
        </p>
        <div class="flex justify-end gap-2 mt-5">
          <NextButton
            ghost
            @click="
              showPurgePeerModal = false;
              purgePeerJid = '';
            "
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.CANCEL_BUTTON') }}
          </NextButton>
          <NextButton
            ruby
            :is-loading="isLoading.purge_peer"
            :disabled="!purgePeerJid.trim()"
            @click="handlePurgePeer"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.PURGE_PEER_CONFIRM') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- Advanced -->
    <div>
      <button
        type="button"
        class="flex items-center gap-2 text-sm font-semibold text-n-slate-11 hover:text-n-slate-12"
        @click="showAdvanced = !showAdvanced"
      >
        <span
          :class="
            showAdvanced ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'
          "
          class="size-4"
        />
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.ADVANCED') }}
      </button>

      <div v-if="showAdvanced" class="mt-4 space-y-5">
        <!-- Metrics -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="grid grid-cols-3 gap-4 text-center">
            <div>
              <div class="text-xs text-n-slate-11 uppercase tracking-wider">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.METRIC_PENDING') }}
              </div>
              <div class="text-2xl font-bold text-n-slate-12 mt-1">
                {{ metrics.pending_webhooks ?? '-' }}
              </div>
            </div>
            <div>
              <div class="text-xs text-n-slate-11 uppercase tracking-wider">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.METRIC_DELIVERED') }}
              </div>
              <div class="text-2xl font-bold text-n-slate-12 mt-1">
                {{ metrics.webhooks_delivered_24h ?? '-' }}
              </div>
            </div>
            <div>
              <div class="text-xs text-n-slate-11 uppercase tracking-wider">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.METRIC_ERRORS') }}
              </div>
              <div
                class="text-2xl font-bold mt-1"
                :class="
                  metrics.errors_24h > 0 ? 'text-n-ruby-11' : 'text-n-slate-12'
                "
              >
                {{ metrics.errors_24h ?? '-' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Identity -->
        <IdentityPanel v-if="status.state === 'connected'" :inbox="inbox" />

        <!-- Diagnostics -->
        <DiagnosticsPanel v-if="status.state === 'connected'" :inbox="inbox" />

        <!-- Signal Stats -->
        <div
          v-if="signalStats.by_type"
          class="p-4 rounded-lg border border-n-weak bg-n-background"
        >
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.SIGNAL_STATS.TITLE') }}
            </h4>
            <span class="text-xs text-n-slate-11">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.SIGNAL_STATS.TOTAL', {
                  total: signalStats.total_keys || 0,
                })
              }}
            </span>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-mono">
            <div
              v-for="(count, type) in signalStats.by_type"
              :key="type"
              class="flex justify-between p-2 rounded bg-n-alpha-2"
            >
              <span class="text-n-slate-11">{{ type }}</span>
              <span class="font-semibold text-n-slate-12">{{ count }}</span>
            </div>
          </div>
        </div>

        <!-- Timeline -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.TIMELINE') }}
            </h4>
            <NextButton
              ghost
              icon="i-lucide-refresh-cw"
              sm
              @click="fetchEvents"
            />
          </div>
          <div
            v-if="events.length === 0"
            class="text-sm text-n-slate-11 py-4 text-center"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.TIMELINE_EMPTY') }}
          </div>
          <ul v-else class="space-y-2 max-h-96 overflow-auto">
            <li
              v-for="ev in events"
              :key="ev.id"
              class="flex items-start gap-3 text-sm"
            >
              <span
                class="size-4 mt-0.5 shrink-0"
                :class="[eventIcon(ev.type).icon, eventIcon(ev.type).cls]"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-mono text-n-slate-12 truncate">{{
                    ev.type
                  }}</span>
                  <span class="text-xs text-n-slate-11 shrink-0">{{
                    formatDate(ev.created_at)
                  }}</span>
                </div>
                <div
                  v-if="ev.payload && Object.keys(ev.payload).length"
                  class="text-xs text-n-slate-11 truncate font-mono"
                >
                  {{ JSON.stringify(ev.payload) }}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
